<template>
  <div class="app-container dida-activity-rates">
    <dida-module-header
      title="活动价推送核对"
      description="按供应商、酒店和入住日逐条核对活动价源数据、RatePlan 编码及 Dida 最后成功接受的 ARI 快照。"
      :status="moduleStatus"
      :status-text="moduleStatusText"
      :updated-at="lastRefreshedAt"
      auto-refresh-text="已查询结果每 30 秒刷新"
      :loading="loading"
      @refresh="load"
    />

    <el-alert
      title="“已推送”以 Dida 接口成功返回后保存的快照为准；Dida 暂无最终展示回查接口。无快照会区分为待同步或同步后未生成，不能把源数据存在直接当作推送成功。"
      type="warning"
      :closable="false"
      show-icon
      class="evidence-alert"
    />
    <el-alert
      title="活动价按源 product_type 识别：4=活动价，5=企业专属活动价。book_rule 只判断预订时间、星期等可售条件，不参与协议价/活动价分类。"
      type="info"
      :closable="false"
      show-icon
      class="evidence-alert"
    />

    <el-card shadow="never" class="filter-card">
      <div slot="header" class="card-header">
        <div><strong>核对范围</strong><small>生产查询强制限定到单酒店、单入住日，避免活动价大表扫描。</small></div>
        <el-tag type="info" effect="plain">北京时间</el-tag>
      </div>
      <el-form :inline="true" :model="query" size="small" @submit.native.prevent>
        <el-form-item label="供应商" required>
          <el-select v-model="query.supplier" clearable placeholder="请选择" style="width: 145px">
            <el-option v-for="item in suppliers" :key="item.value" :label="item.label" :value="item.value" />
          </el-select>
        </el-form-item>
        <el-form-item label="源酒店编码" required>
          <el-input v-model.trim="query.source_hotel_code" clearable placeholder="精确酒店编码" style="width: 210px" @keyup.enter.native="handleSearch" />
        </el-form-item>
        <el-form-item label="入住日期" required>
          <el-date-picker v-model="query.stay_date" type="date" value-format="yyyy-MM-dd" :clearable="false" style="width: 150px" />
        </el-form-item>
        <el-form-item label="源房型">
          <el-input v-model.trim="query.room_type_code" clearable placeholder="精确编码" style="width: 150px" @keyup.enter.native="handleSearch" />
        </el-form-item>
        <el-form-item label="源产品">
          <el-input v-model.trim="query.product_code" clearable placeholder="精确编码" style="width: 150px" @keyup.enter.native="handleSearch" />
        </el-form-item>
        <el-form-item>
          <el-button type="primary" icon="el-icon-search" :loading="loading" @click="handleSearch">查询活动价</el-button>
          <el-button icon="el-icon-refresh" @click="reset">重置</el-button>
        </el-form-item>
      </el-form>
    </el-card>

    <div v-if="queried" class="metric-grid" aria-label="当前页活动价推送状态">
      <div class="metric-card is-success"><span>已推送可售</span><strong>{{ summary.accepted || 0 }}</strong><small>存在 Status=1 成功快照</small></div>
      <div class="metric-card is-warning"><span>已推送关闭</span><strong>{{ summary.inactive || 0 }}</strong><small>存在 Status=0 成功快照</small></div>
      <div class="metric-card is-danger"><span>同步后无快照</span><strong>{{ summary.excluded || 0 }}</strong><small>需根据原因定位过滤项</small></div>
      <div class="metric-card is-info"><span>待同步 / 待确认</span><strong>{{ summary.pending || 0 }}</strong><small>源更新晚于最近 ARI 成功</small></div>
      <div class="metric-card"><span>最近 ARI 处理</span><strong class="time-value">{{ lastARIResolvedAt || '-' }}</strong><small>实际推送成功 {{ lastARISuccessAt || '-' }}</small></div>
    </div>

    <el-card v-if="queried" shadow="never" class="result-card">
      <div slot="header" class="card-header">
        <div><strong>活动价逐条证据</strong><small>当前页统计；展开可核对完整规则、映射和成功快照。</small></div>
        <el-tag :type="hasAttention ? 'warning' : 'success'">{{ hasAttention ? '存在待核对项' : '当前页正常' }}</el-tag>
      </div>
      <el-table v-loading="loading" :data="items" border stripe size="small" empty-text="该酒店和入住日没有识别到活动价">
        <el-table-column type="expand" width="44">
          <template slot-scope="scope">
            <div class="detail-grid">
              <div><b>源预订规则</b><pre>{{ pretty(scope.row.book_rule_parsed || scope.row.book_rule) }}</pre></div>
              <div><b>源取消规则</b><pre>{{ pretty(scope.row.cancel_rule_parsed || scope.row.cancel_rule) }}</pre></div>
              <div><b>RatePlan 静态映射</b><pre>{{ pretty(scope.row.mapped_rules || []) }}</pre></div>
              <div><b>Dida 已接受 ARI</b><pre>{{ pretty(scope.row.accepted_rates || []) }}</pre></div>
            </div>
          </template>
        </el-table-column>
        <el-table-column prop="stay_date" label="入住日" width="105" fixed="left" />
        <el-table-column label="推送结论" width="145" fixed="left" align="center">
          <template slot-scope="scope"><el-tag size="mini" :type="statusTag(scope.row.review_status)">{{ statusText(scope.row.review_status) }}</el-tag></template>
        </el-table-column>
        <el-table-column prop="room_type_code" label="源房型" min-width="125" show-overflow-tooltip />
        <el-table-column prop="product_code" label="源活动产品" min-width="135" show-overflow-tooltip />
        <el-table-column prop="product_name" label="产品名称" min-width="160" show-overflow-tooltip />
        <el-table-column label="产品类型" min-width="150"><template slot-scope="scope">{{ productTypeName(scope.row.product_type) }}</template></el-table-column>
        <el-table-column label="预订窗口（如有）" min-width="245">
          <template slot-scope="scope">
            <div class="window-copy"><span>{{ activityWindow(scope.row) }}</span><small v-if="scope.row.activity_rule && scope.row.activity_rule.weeks">星期 {{ scope.row.activity_rule.weeks }}</small></div>
          </template>
        </el-table-column>
        <el-table-column prop="advance_rate" label="源价" width="90" align="right" />
        <el-table-column prop="quota" label="房量" width="75" align="center" />
        <el-table-column label="Dida RatePlanCode" min-width="260" show-overflow-tooltip>
          <template slot-scope="scope">{{ (scope.row.dida_rate_plan_codes || []).join('、') || '尚无映射' }}</template>
        </el-table-column>
        <el-table-column label="成功快照" width="105" align="center">
          <template slot-scope="scope">{{ scope.row.accepted_rates_total || 0 }} 条</template>
        </el-table-column>
        <el-table-column label="未推送 / 不可售原因" min-width="360">
          <template slot-scope="scope"><span :class="{ 'reason-warning': scope.row.review_status !== 'accepted' }">{{ (scope.row.reasons || []).join('；') || '-' }}</span></template>
        </el-table-column>
        <el-table-column prop="source_updated_at" label="源更新时间" width="165" />
      </el-table>
      <div class="pagination">
        <el-pagination
          :current-page="query.page"
          :page-size="query.limit"
          :page-sizes="[20, 50, 100]"
          :total="total"
          layout="total, sizes, prev, pager, next"
          @size-change="handleSize"
          @current-change="handlePage"
        />
      </div>
    </el-card>

    <el-empty v-else description="请选择供应商、输入源酒店编码并指定入住日期后查询" />
  </div>
</template>

<script>
import request from '@/utils/request'
import DidaModuleHeader from './DidaModuleHeader.vue'

function localDate() {
  const now = new Date()
  const year = now.getFullYear()
  const month = String(now.getMonth() + 1).padStart(2, '0')
  const day = String(now.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

export default {
  name: 'DidaActivityRates',
  components: { DidaModuleHeader },
  data() {
    return {
      suppliers: [
        { label: '锦江', value: 'jin_jiang' },
        { label: '隧穿亚朵', value: 'sui_chuan' },
        { label: '丽呈', value: 'li_cheng' },
        { label: '龙腾', value: 'long_teng' },
      ],
      query: { supplier: '', source_hotel_code: '', stay_date: localDate(), room_type_code: '', product_code: '', page: 1, limit: 20 },
      items: [],
      total: 0,
      summary: {},
      lastARISuccessAt: null,
      lastARIResolvedAt: null,
      loading: false,
      queried: false,
      error: '',
      lastRefreshedAt: '',
      refreshTimer: null,
      requestId: 0,
    }
  },
  computed: {
    hasAttention() {
      return Number(this.summary.excluded || 0) > 0 || Number(this.summary.pending || 0) > 0
    },
    moduleStatus() {
      if (this.error) return 'danger'
      if (!this.queried) return 'loading'
      return this.hasAttention ? 'warning' : 'healthy'
    },
    moduleStatusText() {
      if (this.error) return '活动价证据读取失败'
      if (!this.queried) return '等待指定核对范围'
      return this.hasAttention ? '存在待核对活动价' : '活动价快照正常'
    },
  },
  created() {
    const routeQuery = (this.$route && this.$route.query) || {}
    ;['supplier', 'source_hotel_code', 'stay_date', 'room_type_code', 'product_code'].forEach(key => {
      if (routeQuery[key]) this.query[key] = String(routeQuery[key])
    })
    if (this.validScope(false)) this.load()
    this.refreshTimer = setInterval(() => {
      if (this.queried && !document.hidden) this.load(false)
    }, 30000)
  },
  beforeDestroy() {
    if (this.refreshTimer) clearInterval(this.refreshTimer)
    this.requestId += 1
  },
  methods: {
    validScope(notify = true) {
      const valid = !!(this.query.supplier && this.query.source_hotel_code && this.query.stay_date)
      if (!valid && notify) this.$message.warning('请先选择供应商，并填写源酒店编码和入住日期')
      return valid
    },
    handleSearch() {
      if (!this.validScope()) return
      this.query.page = 1
      this.load()
    },
    load(showLoading = true) {
      if (!this.validScope(false)) return Promise.resolve()
      const requestId = ++this.requestId
      if (showLoading) this.loading = true
      this.error = ''
      return request({
        url: '/api/backend/dida/hotel-publish-controls/source-statuses',
        method: 'get',
        params: { ...this.query, activity_only: 1 },
      }).then(response => {
        if (requestId !== this.requestId) return
        const data = response.data || {}
        this.items = data.items || []
        this.total = Number(data.total || 0)
        this.summary = data.page_summary || {}
        this.lastARISuccessAt = data.last_ari_success_at || null
        this.lastARIResolvedAt = data.last_ari_resolved_at || data.last_ari_success_at || null
        this.queried = true
        this.lastRefreshedAt = new Date().toLocaleString()
      }).catch(() => {
        if (requestId !== this.requestId) return
        this.error = '活动价推送证据读取失败，请检查后台和数据库状态'
      }).finally(() => { if (requestId === this.requestId) this.loading = false })
    },
    reset() {
      this.requestId += 1
      this.query = { supplier: '', source_hotel_code: '', stay_date: localDate(), room_type_code: '', product_code: '', page: 1, limit: 20 }
      this.items = []
      this.total = 0
      this.summary = {}
      this.lastARISuccessAt = null
      this.lastARIResolvedAt = null
      this.queried = false
      this.loading = false
      this.error = ''
    },
    handleSize(value) { this.query.limit = value; this.query.page = 1; this.load() },
    handlePage(value) { this.query.page = value; this.load() },
    statusText(value) {
      return { accepted: '已推送可售', inactive: '已推送关闭', excluded: '同步后无快照', pending: '待同步/待确认' }[value] || '未知'
    },
    statusTag(value) {
      return { accepted: 'success', inactive: 'warning', excluded: 'danger', pending: 'info' }[value] || 'info'
    },
    activityWindow(row) {
      const rule = row.activity_rule || {}
      const start = rule.start_at || '未限制开始'
      const end = rule.end_at || '未限制结束'
      return `${start} ～ ${end}`
    },
    productTypeName(value) {
      return {
        0: '0 · 本店协议价',
        1: '1 · 批量分店品牌协议价',
        2: '2 · 组合品牌协议价',
        3: '3 · 平台协议价',
        4: '4 · 活动价',
        5: '5 · 企业专属活动价',
      }[String(value)] || `未知类型 · ${value === null || value === undefined || value === '' ? '-' : value}`
    },
    pretty(value) {
      if (typeof value === 'string') {
        try { return JSON.stringify(JSON.parse(value), null, 2) } catch (error) { return value || '-' }
      }
      return JSON.stringify(value === undefined || value === null ? {} : value, null, 2)
    },
  },
}
</script>

<style lang="scss" scoped>
.evidence-alert, .filter-card, .metric-grid { margin-bottom: 16px; }
.card-header { display: flex; align-items: center; justify-content: space-between; gap: 16px; }
.card-header > div { display: flex; min-width: 0; flex-direction: column; gap: 5px; }
.card-header small { color: #909399; font-weight: 400; }
.filter-card ::v-deep .el-form-item { margin-bottom: 4px; }
.metric-grid { display: grid; grid-template-columns: repeat(5, minmax(170px, 1fr)); gap: 12px; }
.metric-card { min-height: 102px; padding: 15px; border: 1px solid #e4e7ed; border-top: 4px solid #6f8fae; border-radius: 6px; background: #fff; }
.metric-card.is-success { border-top-color: #67c23a; }
.metric-card.is-warning { border-top-color: #e6a23c; }
.metric-card.is-danger { border-top-color: #f56c6c; }
.metric-card.is-info { border-top-color: #909399; }
.metric-card span, .metric-card small { display: block; color: #909399; font-size: 12px; }
.metric-card strong { display: block; margin: 8px 0 5px; color: #303133; font-size: 27px; }
.metric-card .time-value { overflow: hidden; font-size: 15px; line-height: 27px; text-overflow: ellipsis; white-space: nowrap; }
.result-card { margin-bottom: 20px; }
.window-copy { display: flex; flex-direction: column; gap: 4px; }
.window-copy small { color: #909399; }
.reason-warning { color: #c45656; }
.detail-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 12px; padding: 8px 18px; }
.detail-grid > div { min-width: 0; }
.detail-grid b { display: block; margin-bottom: 6px; color: #606266; }
.detail-grid pre { max-height: 230px; margin: 0; overflow: auto; padding: 10px; color: #303133; border: 1px solid #ebeef5; border-radius: 4px; background: #f7f9fc; white-space: pre-wrap; word-break: break-all; }
.pagination { display: flex; justify-content: flex-end; margin-top: 16px; }
@media (max-width: 1400px) { .metric-grid { grid-template-columns: repeat(3, 1fr); } }
@media (max-width: 900px) { .metric-grid, .detail-grid { grid-template-columns: 1fr; } .card-header { align-items: flex-start; flex-direction: column; } }
</style>
